import type { ConsentCookieDecodeResult } from './cookie-codec.js';
import type {
  ConsentConfiguration,
  ConsentRequiredReason,
  ConsentState,
} from './types.js';

const MILLISECONDS_PER_DAY = 86_400_000;

export type ConsentRequirementResult =
  | { readonly status: 'valid'; readonly state: ConsentState }
  | {
      readonly status: 'required';
      readonly reason: Extract<
        ConsentRequiredReason,
        | 'state_missing'
        | 'state_invalid'
        | 'state_expired'
        | 'reconsent_required'
      >;
    };

export class ConsentRequirementEvaluationError extends Error {
  readonly code = 'invalid_now';

  constructor() {
    super('Consent requirement clock must be a valid Date.');
    this.name = 'ConsentRequirementEvaluationError';
  }
}

export function evaluateConsentRequirement(
  config: ConsentConfiguration,
  stored: ConsentCookieDecodeResult,
  now: Date = new Date(),
): ConsentRequirementResult {
  if (Number.isNaN(now.getTime())) {
    throw new ConsentRequirementEvaluationError();
  }

  if (!stored.ok) {
    return Object.freeze({
      status: 'required',
      reason: stored.reason === 'missing' ? 'state_missing' : 'state_invalid',
    });
  }

  const expiresAt =
    Date.parse(stored.state.updatedAt) +
    config.consentLifetimeDays * MILLISECONDS_PER_DAY;

  if (expiresAt <= now.getTime()) {
    return Object.freeze({ status: 'required', reason: 'state_expired' });
  }

  if (
    stored.state.configurationVersion < config.minimumValidConsentVersion
  ) {
    return Object.freeze({
      status: 'required',
      reason: 'reconsent_required',
    });
  }

  return Object.freeze({ status: 'valid', state: stored.state });
}

